import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, fetchSmsMediaBlob } from '../api';

const btn: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  backgroundColor: 'transparent',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 600,
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontFamily: 'inherit',
  fontSize: 'var(--font-size-sm)',
  boxSizing: 'border-box',
};

function fmt(ts: string | null) {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/** HEIC/HEIF (iPhone photos) can't be decoded by browsers or <canvas>. Detect
 *  it and convert to a JPEG blob via heic2any (lazy-loaded), so it renders in
 *  the thread and can be downscaled/sent as a normal image. */
export function isHeic(type?: string | null, name?: string | null): boolean {
  const t = (type || '').toLowerCase();
  return t.includes('heic') || t.includes('heif') || /\.(heic|heif)$/i.test(name || '');
}
export async function heicToJpegBlob(input: Blob): Promise<Blob> {
  const { default: heic2any } = await import('heic2any');
  const out = await heic2any({ blob: input, toType: 'image/jpeg', quality: 0.85 });
  return Array.isArray(out) ? out[0] : (out as Blob);
}

/**
 * Downscale an image and re-encode as JPEG so the payload is small and
 * MMS-friendly. If the browser can't decode it (e.g. HEIC in Chrome), fall back
 * to the original bytes unchanged.
 */
export async function prepareImage(file: File, maxDim = 1600, quality = 0.8): Promise<{ base64: string; mime: string; name: string }> {
  // HEIC/HEIF → JPEG first, so canvas can decode + downscale it (and it renders).
  let source: Blob = file;
  if (isHeic(file.type, file.name)) {
    try { source = await heicToJpegBlob(file); } catch { /* fall back to raw below */ }
  }
  try {
    const bitmap = await createImageBitmap(source);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no canvas context');
    ctx.drawImage(bitmap, 0, 0, w, h);
    const base64 = canvas.toDataURL('image/jpeg', quality).split(',')[1] ?? '';
    if (!base64) throw new Error('encode failed');
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return { base64, mime: 'image/jpeg', name };
  } catch {
    // Couldn't downscale — send the source bytes as-is (a converted JPEG if HEIC
    // conversion succeeded, otherwise the original file).
    const converted = source !== file;
    return {
      base64: await fileToBase64(source),
      mime: source.type || file.type || 'application/octet-stream',
      name: converted ? file.name.replace(/\.[^.]+$/, '') + '.jpg' : file.name,
    };
  }
}

export function SmsMedia({ media, out }: { media: { src: string; contentType: string; filename?: string | null }[]; out?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', margin: '4px 0' }}>
      {media.map((m, i) => <SmsMediaItem key={i} mediaSrc={m.src} contentType={m.contentType} filename={m.filename ?? null} out={out} />)}
    </div>
  );
}

/** A friendly label for a non-image attachment when no filename was stored. */
function attachmentLabel(contentType: string): string {
  if (/pdf/i.test(contentType)) return 'PDF document';
  const sub = contentType.split('/')[1] || 'file';
  return `${sub.toUpperCase()} file`;
}

function SmsMediaItem({ mediaSrc, contentType, filename, out }: { mediaSrc: string; contentType: string; filename?: string | null; out?: boolean }) {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    let obj: string | null = null;
    fetchSmsMediaBlob(mediaSrc)
      .then(async (blob) => {
        // HEIC won't render in an <img> — convert to JPEG for display.
        const b = (isHeic(contentType, undefined) || isHeic(blob.type)) ? await heicToJpegBlob(blob).catch(() => blob) : blob;
        if (!active) return;
        obj = URL.createObjectURL(b);
        setSrc(obj);
      })
      .catch(() => { if (active) setErr(true); });
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [mediaSrc, contentType]);
  if (err) return <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>[attachment unavailable]</span>;
  if (!src) return <div style={{ width: 96, height: 96, background: 'var(--color-background)', border: '1px solid var(--color-border)', borderRadius: 6 }} />;
  if (contentType.startsWith('image/')) {
    return (
      <a href={src} target="_blank" rel="noreferrer">
        <img src={src} alt="attachment" style={{ maxWidth: 140, maxHeight: 140, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--color-border)', display: 'block' }} />
      </a>
    );
  }
  const label = (filename && filename.trim()) || attachmentLabel(contentType);
  return (
    <a
      href={src}
      download={filename || undefined}
      target="_blank"
      rel="noreferrer"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%',
        fontSize: 'var(--font-size-sm)', fontWeight: 600, textDecoration: 'underline',
        // Visible on both bubbles: white on the gold outbound bubble, accent on inbound.
        color: out ? '#fff' : 'var(--color-accent)',
        background: out ? 'rgba(255,255,255,0.18)' : 'var(--color-surface)',
        border: `1px solid ${out ? 'rgba(255,255,255,0.5)' : 'var(--color-border)'}`,
        borderRadius: 6, padding: '4px 8px', overflowWrap: 'anywhere',
      }}
    >
      📄 {label}
    </a>
  );
}

/** Compose + send an outbound SMS/MMS to a contact. Larger, comfortable text area. */
export function SmsComposer({ phone, customerId, contactName, onSent }: { phone: string; customerId: number | null; contactName?: string | null; onSent?: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [images, setImages] = useState<{ base64: string; mime: string; name: string }[]>([]);
  const send = useMutation({
    mutationFn: (force?: boolean) => api.sendOutboundSms({ phone, customerId, body: text, images: images.map(({ base64, mime, name }) => ({ base64, mime, name })), force }),
    onSuccess: (r) => {
      if (r.ok) {
        setText('');
        setImages([]);
        qc.invalidateQueries({ queryKey: ['sms-threads'] });
        qc.invalidateQueries({ queryKey: ['sms-thread', phone] });
        onSent?.();
      }
    },
  });
  const [preparing, setPreparing] = useState(false);
  const onFiles = async (files: FileList | null) => {
    if (!files) return;
    setPreparing(true);
    try {
      const picked: { base64: string; mime: string; name: string }[] = [];
      for (const f of Array.from(files).slice(0, 10)) picked.push(await prepareImage(f));
      setImages((prev) => [...prev, ...picked].slice(0, 10));
    } finally {
      setPreparing(false);
    }
  };
  const res = send.data;
  const needsConfirm = res && !res.ok && res.unsubscribed;
  // Rough payload size (base64 chars ≈ bytes); MMS realistically wants the total
  // under ~5MB, and the request itself is capped server-side.
  const approxBytes = images.reduce((n, im) => n + im.base64.length * 0.75, 0);
  const tooBig = approxBytes > 5 * 1024 * 1024;
  const canSend = (text.trim() || images.length > 0) && !send.isPending && !preparing && !tooBig;
  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        placeholder="Type a message…"
        style={{ ...inputStyle, minHeight: 130, resize: 'vertical' }}
      />
      {images.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {images.map((im, i) => (
            <span key={i} style={{ fontSize: 'var(--font-size-xs)', background: 'var(--color-background)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '2px 6px' }}>
              📎 {im.name} <button onClick={() => setImages((p) => p.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error)' }}>✕</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ ...btn, cursor: 'pointer' }}>
          {preparing ? 'Preparing…' : '📎 Attach'}
          <input type="file" accept="image/*,application/pdf,.pdf" multiple style={{ display: 'none' }} onChange={(e) => onFiles(e.target.files)} />
        </label>
        {!needsConfirm ? (
          <button style={{ ...btn, background: 'var(--color-accent)', color: '#fff', borderColor: 'var(--color-accent)' }} disabled={!canSend} onClick={() => send.mutate(undefined)}>
            {send.isPending ? 'Sending…' : 'Send'}
          </button>
        ) : (
          <>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error)' }}>Unsubscribed from SMS.</span>
            <button style={{ ...btn, background: 'var(--color-error)', color: '#fff', borderColor: 'var(--color-error)' }} disabled={send.isPending} onClick={() => send.mutate(true)}>Send anyway</button>
          </>
        )}
        {tooBig && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error)' }}>Attachments too large for one message — remove some (aim under ~5MB total).</span>}
        {res?.ok && res.viaLink && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Sent as a link (MMS not supported on this number).</span>}
        {res?.ok && !res.viaLink && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-accent)' }}>Sent ✓</span>}
        {send.isError && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error)' }}>{(send.error as Error).message}</span>}
      </div>
    </div>
  );
}

/** Full SMS conversation with one contact: message timeline + composer. Used in
 *  the Assistant SMS tab and inside the contact detail. */
export function SmsConversation({ phone, customerId, contactName }: { phone: string; customerId: number | null; contactName?: string | null }) {
  const { data: thread, isLoading } = useQuery({ queryKey: ['sms-thread', phone], queryFn: () => api.getSmsThread(phone), refetchInterval: 10_000, refetchIntervalInBackground: true, staleTime: 0 });
  const messages = thread?.messages ?? [];
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Note: opening a conversation no longer auto-marks it read. It stays in the
  // "To reply" queue until it's explicitly dismissed (or a reply is sent), so
  // reading a message doesn't make it silently disappear from the action list.
  // Keep the newest message in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, thread]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)', minWidth: 0, maxWidth: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>Text messages</strong>
      </div>
      <div ref={scrollRef} style={{ maxHeight: 320, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 6, padding: 'var(--spacing-2)', background: 'var(--color-background)', borderRadius: 'var(--radius-default)', width: '100%', boxSizing: 'border-box' }}>
        {isLoading ? (
          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>Loading…</span>
        ) : messages.length === 0 ? (
          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>No messages yet. Send the first one below.</span>
        ) : (
          messages.map((m, idx) => {
            const out = m.direction === 'out';
            return (
              <div key={idx} style={{ alignSelf: out ? 'flex-end' : 'flex-start', maxWidth: '80%', background: out ? 'var(--color-accent)' : 'var(--color-surface-elevated)', color: out ? 'var(--color-text-inverse)' : 'var(--color-text)', border: out ? 'none' : '1px solid var(--color-border)', borderRadius: 12, padding: '6px 10px', overflowWrap: 'anywhere', wordBreak: 'break-word', userSelect: 'text' }}>
                {m.body && <div style={{ fontSize: 'var(--font-size-sm)', whiteSpace: 'pre-wrap', userSelect: 'text' }}>{m.body}</div>}
                {m.media && m.media.length > 0 && <SmsMedia media={m.media} out={out} />}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', fontSize: 'var(--font-size-xs)', color: out ? 'rgba(255,255,255,0.85)' : 'var(--color-text-muted)', marginTop: 2 }}>
                  {m.body && (
                    <button
                      onClick={() => { navigator.clipboard?.writeText(m.body).catch(() => {}); }}
                      title="Copy message text"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', textDecoration: 'underline', fontSize: 'var(--font-size-xs)' }}
                    >
                      Copy
                    </button>
                  )}
                  <span>{out ? 'You · ' : ''}{fmt(m.at)}{out && m.status ? ` · ${m.status}` : ''}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
      <SmsComposer phone={phone} customerId={customerId} contactName={contactName} />
    </div>
  );
}

const inboxCard: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

/** Full SMS inbox: every conversation, newest first (like a messaging app).
 *  Click a row to open its thread + composer inline (reuses SmsConversation). */
export function SmsInbox({ onOpenContact }: { onOpenContact?: (customerId: number) => void }) {
  const [open, setOpen] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  // Debounce so we don't hit the backend on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const searching = debounced.length > 0;
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['sms-threads', debounced],
    queryFn: () => api.listSmsThreads(debounced || undefined),
    // Only auto-refresh the live inbox; a search result shouldn't keep re-running.
    refetchInterval: searching ? false : 10_000,
    refetchIntervalInBackground: true,
    staleTime: 0,
  });
  const threads = data?.threads ?? [];
  const searchBox = (
    <input
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder="Search messages, name or number…"
      style={{ ...inputStyle, marginBottom: 'var(--spacing-3)' }}
    />
  );
  if (isLoading) return <section style={inboxCard}>{searchBox}<p style={{ color: 'var(--color-text-muted)' }}>Loading…</p></section>;
  return (
    <section style={inboxCard}>
      {searchBox}
      {threads.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>
          {searching ? `No conversations match “${debounced}”.` : 'No conversations yet. Inbound and sent texts appear here.'}
        </p>
      ) : (
      <div style={{ display: 'grid', gap: 2 }}>
        {searching && (
          <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', padding: '0 var(--spacing-2) var(--spacing-1)' }}>
            {threads.length} conversation{threads.length === 1 ? '' : 's'} matching “{debounced}”{isFetching ? ' · searching…' : ''}
          </div>
        )}
        {threads.map((t, i) => {
          const isOpen = open === t.phone;
          return (
            <div key={t.phone} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--color-border)', minWidth: 0 }}>
              <div
                onClick={() => setOpen(isOpen ? null : t.phone)}
                title="Open conversation"
                style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--spacing-2)', alignItems: 'center', cursor: 'pointer', padding: 'var(--spacing-2)', minWidth: 0 }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div>
                    {t.customerId != null && onOpenContact ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); onOpenContact(t.customerId as number); }}
                        title="Open this contact"
                        style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 700, color: 'var(--color-accent)', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {t.customerName ?? t.phone}
                      </button>
                    ) : (
                      <strong style={{ color: 'var(--color-text)' }}>{t.customerName ?? t.phone}</strong>
                    )}
                    {t.unread > 0 && <span style={{ marginLeft: 6, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#fff', background: 'var(--color-error)', borderRadius: 10, padding: '0 6px' }}>{t.unread}</span>}
                    <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}> · {fmt(t.lastAt)}</span>
                  </div>
                  <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.lastDirection === 'out' ? '↩ ' : ''}{t.lastBody || '(no text)'}
                  </div>
                </div>
                {t.customerId != null && onOpenContact && (
                  <button style={btn} onClick={(e) => { e.stopPropagation(); onOpenContact(t.customerId as number); }}>Contact</button>
                )}
                <span style={{ color: 'var(--color-text-muted)' }}>{isOpen ? '▾' : '▸'}</span>
              </div>
              {isOpen && (
                <div style={{ padding: '0 var(--spacing-2) var(--spacing-2)', minWidth: 0, overflow: 'hidden' }}>
                  <SmsConversation phone={t.phone} customerId={t.customerId} contactName={t.customerName} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </section>
  );
}
